import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone, Clock, AlertCircle } from 'lucide-react';

const API = "https://safesheatlas.com/api";

const EmergencyDirectory = ({ destinationId }) => {
  const [contacts, setContacts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmergencyContacts();
  }, [destinationId]);

  const fetchEmergencyContacts = async () => {
    try {
      const response = await axios.get(`${API}/emergency/${destinationId}`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching emergency contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const serviceIcons = {
    police: '👮',
    ambulance: '🚑',
    womens_helpline: '👩',
    embassy: '🏢',
    tourist_police: '👮‍♀️',
    crisis_center: '☎️'
  };

  const serviceLabels = {
    police: 'Police',
    ambulance: 'Ambulance',
    womens_helpline: "Women's Helpline",
    embassy: 'Embassy',
    tourist_police: 'Tourist Police',
    crisis_center: 'Crisis Center'
  };

  if (loading) {
    return <div className="text-center py-8">Loading emergency contacts...</div>;
  }

  const hasContacts = Object.keys(contacts).length > 0;

  return (
    <div className="space-y-6" data-testid="emergency-directory">
      {!hasContacts && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-800 font-medium">Emergency contacts being updated</p>
            <p className="text-xs text-yellow-700 mt-1">We're currently compiling emergency contact information for this destination.</p>
          </div>
        </div>
      )}

      {Object.entries(contacts).map(([serviceType, serviceContacts]) => (
        <div key={serviceType} className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">{serviceIcons[serviceType]}</span>
            <h3 className="text-xl font-bold">{serviceLabels[serviceType]}</h3>
          </div>

          <div className="space-y-4">
            {serviceContacts.map((contact, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{contact.service_name}</h4>
                    {contact.additional_info && (
                      <p className="text-sm text-gray-600 mt-1">{contact.additional_info}</p>
                    )}
                  </div>
                  {contact.is_24_7 && (
                    <div className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      <Clock className="w-3 h-3" />
                      <span>24/7</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-purple-600" />
                  <a
                    href={`tel:${contact.phone_number}`}
                    className="text-lg font-bold text-purple-600 hover:text-purple-700"
                  >
                    {contact.phone_number}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-semibold mb-1">In case of emergency:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Call emergency services immediately</li>
              <li>Contact your embassy if you're in serious danger</li>
              <li>Share your location with trusted contacts</li>
              <li>Save these numbers in your phone before traveling</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyDirectory;